import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Helmet } from 'react-helmet';
import { FaCar, FaMoneyBillWave, FaChartBar, FaQuestionCircle, FaCalendar } from 'react-icons/fa';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const CarLoanCalculator = () => {
  const [formData, setFormData] = useState({
    carPrice: '',
    downPayment: '',
    interestRate: '',
    loanTerm: ''
  });

  const [results, setResults] = useState(null);
  const [error, setError] = useState('');
  const [chartData, setChartData] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const calculateCarLoan = () => {
    const { carPrice, downPayment, interestRate, loanTerm } = formData;

    if (!carPrice || !downPayment || !interestRate || !loanTerm) {
      setError('Please fill in all required fields');
      return;
    }

    const price = parseFloat(carPrice);
    const payment = parseFloat(downPayment);
    const rate = parseFloat(interestRate) / 100 / 12;
    const term = parseFloat(loanTerm) * 12;

    if (isNaN(price) || isNaN(payment) || isNaN(rate) || isNaN(term) || price <= 0 || payment < 0 || rate <= 0 || term <= 0) {
      setError('Please enter valid numbers for all fields');
      return;
    }

    const loanAmount = price - payment;
    const monthlyRate = rate;
    const monthlyPayment = (loanAmount * monthlyRate * Math.pow(1 + monthlyRate, term)) / (Math.pow(1 + monthlyRate, term) - 1);
    const totalPayments = monthlyPayment * term;
    const totalInterest = totalPayments - loanAmount;

    setResults({
      carPrice: `$${price.toFixed(2)}`,
      downPayment: `$${payment.toFixed(2)}`,
      loanAmount: `$${loanAmount.toFixed(2)}`,
      interestRate: `${rate * 12 * 100}%`,
      loanTerm: `${loanTerm} years`,
      monthlyPayment: `$${monthlyPayment.toFixed(2)}`,
      totalPayments: `$${totalPayments.toFixed(2)}`,
      totalInterest: `$${totalInterest.toFixed(2)}`
    });

    setChartData({
      labels: ['Loan Amount', 'Total Interest', 'Total Payments'],
      datasets: [{
        label: 'Car Loan Breakdown',
        data: [loanAmount, totalInterest, totalPayments],
        backgroundColor: ['rgba(75, 192, 192, 0.2)', 'rgba(255, 99, 132, 0.2)', 'rgba(54, 162, 235, 0.2)'],
        borderColor: ['rgb(75, 192, 192)', 'rgb(255, 99, 132)', 'rgb(54, 162, 235)'],
        borderWidth: 1
      }]
    });
  };

  return (
    <div>
      <Helmet>
        <title>Car Loan Calculator | Financial Tool</title>
        <meta name="description" content="Calculate your car loan payments, total interest, and total payments over the life of the loan with our Car Loan Calculator." />
        <meta name="keywords" content="Car loan calculator, financial tool, auto loan calculator" />

        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://yourdomain.com/car-loan-calculator" />
        <meta property="og:title" content="Car Loan Calculator" />
        <meta property="og:description" content="Calculate your car loan payments, total interest, and total payments over the life of the loan with our Car Loan Calculator." />
        <meta property="og:image" content="https://yourdomain.com/preview.jpg" />

        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content="https://yourdomain.com/car-loan-calculator" />
        <meta property="twitter:title" content="Car Loan Calculator" />
        <meta property="twitter:description" content="Calculate your car loan payments, total interest, and total payments over the life of the loan with our Car Loan Calculator." />
        <meta property="twitter:image" content="https://yourdomain.com/preview.jpg" />
      </Helmet>

      <div className="math-calculators max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="calculator-header mb-12">
          <div className="flex items-center gap-4">
            <div className="header-icon text-4xl">
              <FaCar />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Car Loan Calculator</h1>
              <p className="text-lg text-blue-600 font-semibold">Calculate Your Car Loan Payments</p>
            </div>
          </div>
        </div>

        <div className="calculator-description space-y-4 mb-12">
          <p className="text-gray-700">
            The Car Loan Calculator helps you estimate your monthly car loan payments, total interest, and total payments over the life of the loan.
          </p>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="calculator-form max-w-2xl mx-auto bg-white rounded-xl shadow-lg p-8"
        >
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="carPrice">
                <FaMoneyBillWave className="inline-block mr-2" /> Car Price
              </label>
              <input
                type="number"
                id="carPrice"
                name="carPrice"
                value={formData.carPrice}
                onChange={handleInputChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="downPayment">
                <FaMoneyBillWave className="inline-block mr-2" /> Down Payment
              </label>
              <input
                type="number"
                id="downPayment"
                name="downPayment"
                value={formData.downPayment}
                onChange={handleInputChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="interestRate">
                <FaChartBar className="inline-block mr-2" /> Interest Rate (%)
              </label>
              <input
                type="number"
                id="interestRate"
                name="interestRate"
                value={formData.interestRate}
                onChange={handleInputChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="loanTerm">
                <FaCalendar className="inline-block mr-2" /> Loan Term (years)
              </label>
              <input
                type="number"
                id="loanTerm"
                name="loanTerm"
                value={formData.loanTerm}
                onChange={handleInputChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
              />
            </div>

            <button
              onClick={calculateCarLoan}
              className="w-full bg-green-600 text-white py-3 px-4 rounded-md text-sm font-semibold hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
            >
              Calculate Car Loan
            </button>

            {error && (
              <div className="text-red-500 text-center mt-4">
                {error}
              </div>
            )}
          </div>

          {results && (
            <div className="mt-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Results</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
                <div className="bg-white p-6 rounded-xl shadow-md">
                  <h3 className="text-xl font-semibold mb-4">Car Price</h3>
                  <p className="text-xl font-medium">{results.carPrice}</p>
                </div>

                <div className="bg-white p-6 rounded-xl shadow-md">
                  <h3 className="text-xl font-semibold mb-4">Down Payment</h3>
                  <p className="text-xl font-medium">{results.downPayment}</p>
                </div>

                <div className="bg-white p-6 rounded-xl shadow-md">
                  <h3 className="text-xl font-semibold mb-4">Loan Amount</h3>
                  <p className="text-xl font-medium">{results.loanAmount}</p>
                </div>

                <div className="bg-white p-6 rounded-xl shadow-md">
                  <h3 className="text-xl font-semibold mb-4">Interest Rate</h3>
                  <p className="text-xl font-medium">{results.interestRate}</p>
                </div>

                <div className="bg-white p-6 rounded-xl shadow-md">
                  <h3 className="text-xl font-semibold mb-4">Loan Term</h3>
                  <p className="text-xl font-medium">{results.loanTerm}</p>
                </div>

                <div className="bg-white p-6 rounded-xl shadow-md">
                  <h3 className="text-xl font-semibold mb-4">Monthly Payment</h3>
                  <p className="text-xl font-medium">{results.monthlyPayment}</p>
                </div>

                <div className="bg-white p-6 rounded-xl shadow-md">
                  <h3 className="text-xl font-semibold mb-4">Total Payments</h3>
                  <p className="text-xl font-medium">{results.totalPayments}</p>
                </div>

                <div className="bg-white p-6 rounded-xl shadow-md">
                  <h3 className="text-xl font-semibold mb-4">Total Interest</h3>
                  <p className="text-xl font-medium">{results.totalInterest}</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-12 mb-12">
                <div>
                  <h3 className="text-xl font-semibold mb-4">Car Loan Breakdown</h3>
                  <div className="h-96">
                    <Bar
                      data={chartData}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        scales: {
                          y: {
                            beginAtZero: true,
                            title: {
                              display: true,
                              text: 'Amount'
                            }
                          },
                          x: {
                            title: {
                              display: true,
                              text: 'Categories'
                            }
                          }
                        },
                        plugins: {
                          title: {
                            display: true,
                            text: 'Car Loan Breakdown'
                          }
                        }
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
        </motion.div>

        <div className="calculator-section mt-24">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
            How to Use Car Loan Calculator <span className="ml-2 text-2xl">📋</span>
          </h2>
          <div className="bg-white p-8 rounded-xl shadow-md">
            <p className="text-gray-700 mb-6">
              Follow these steps to use the Car Loan Calculator effectively:
            </p>
            
            <div className="space-y-8">
              <div>
                <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 1: Enter Car Price</h3>
                <ul className="list-disc pl-8 space-y-2">
                  <li>Enter the total price of the car.</li>
                </ul>
              </div>
              
              <div>
                <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 2: Enter Down Payment</h3>
                <ul className="list-disc pl-8 space-y-2">
                  <li>Enter the amount you are planning to pay as a down payment.</li>
                </ul>
              </div>
              
              <div>
                <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 3: Enter Interest Rate</h3>
                <ul className="list-disc pl-8 space-y-2">
                  <li>Enter the annual interest rate of the car loan.</li>
                </ul>
              </div>
              
              <div>
                <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 4: Enter Loan Term</h3>
                <ul className="list-disc pl-8 space-y-2">
                  <li>Enter the loan term in years.</li>
                </ul>
              </div>
              
              <div>
                <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 5: Calculate</h3>
                <ul className="list-disc pl-8 space-y-2">
                  <li>Click the "Calculate Car Loan" button to get the results.</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <div className="calculator-section mt-24">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
            Features and Benefits <span className="ml-2 text-2xl">✨</span>
          </h2>
          <div className="bg-white p-8 rounded-xl shadow-md">
            <ul className="list-disc pl-8 space-y-4">
              <li className="flex items-center">
                <span className="h-3 w-3 bg-green-600 rounded-full mr-2"></span>
                <span className="font-medium">Quick Calculation:</span> Instantly calculate your monthly payments and total interest.
              </li>
              <li className="flex items-center">
                <span className="h-3 w-3 bg-blue-600 rounded-full mr-2"></span>
                <span className="font-medium">Visualization:</span> Graphical representation of your car loan breakdown.
              </li>
              <li className="flex items-center">
                <span className="h-3 w-3 bg-yellow-600 rounded-full mr-2"></span>
                <span className="font-medium">User-Friendly:</span> Simple and intuitive interface for easy use.
              </li>
              <li className="flex items-center">
                <span className="h-3 w-3 bg-red-600 rounded-full mr-2"></span>
                <span className="font-medium">Educational:</span> Helps you understand the financial implications of different car loan terms.
              </li>
            </ul>
          </div>
        </div>

        <div className="calculator-section mt-24">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
            Frequently Asked Questions (FAQ) <span className="ml-2 text-2xl">❓</span>
          </h2>
          <div className="bg-white p-8 rounded-xl shadow-md">
            <div className="space-y-8">
              <div>
                <h3 className="text-xl font-semibold text-blue-600 mb-4">What is a car loan calculator?</h3>
                <p className="text-gray-700">
                  A car loan calculator is a tool that helps you estimate your monthly car loan payments, total interest, and total payments over the life of the loan.
                </p>
              </div>
              
              <div>
                <h3 className="text-xl font-semibold text-blue-600 mb-4">How accurate is the car loan calculator?</h3>
                <p className="text-gray-700">
                  The car loan calculator provides an estimate based on the inputs provided. Actual loan terms may vary based on lender policies and other factors.
                </p>
              </div>
              
              <div>
                <h3 className="text-xl font-semibold text-blue-600 mb-4">Can I use this calculator for different types of car loans?</h3>
                <p className="text-gray-700">
                  Yes, this calculator can be used for various types of car loans, including new and used car loans.
                </p>
              </div>
              
              <div>
                <h3 className="text-xl font-semibold text-blue-600 mb-4">Is this calculator free to use?</h3>
                <p className="text-gray-700">
                  Yes, this calculator is completely free to use.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CarLoanCalculator;